import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Code,
  ShoppingCart,
  Smartphone,
  Search,
  BarChart3,
  Shield,
  Check,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Zap,
} from 'lucide-react';

const Services = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in');
    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        elements.forEach((el) => observerRef.current.unobserve(el));
      }
    };
  }, []);

  const services = [
    {
      icon: <Code size={48} />,
      title: 'Web Development',
      description:
        'Custom websites and web applications built with modern frameworks. Responsive, fast, and SEO-optimized.',
      features: [
        'Responsive Design',
        'SEO Optimization',
        'Performance Tuning',
        'Cross-browser Compatibility',
      ],
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      icon: <ShoppingCart size={48} />,
      title: 'E-commerce Solutions',
      description:
        'Complete online store solutions with payment processing, inventory management, and order tracking.',
      features: [
        'Payment Integration',
        'Inventory Management',
        'Order Tracking',
        'Customer Portal',
      ],
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: <Smartphone size={48} />,
      title: 'Custom Applications',
      description:
        'Tailored software solutions designed to solve your specific business challenges and streamline operations.',
      features: [
        'Custom Workflows',
        'API Integration',
        'Data Analytics',
        'Scalable Architecture',
      ],
      gradient: 'from-green-500 to-emerald-500',
    },
    {
      icon: <Search size={48} />,
      title: 'SEO & Marketing',
      description:
        'Boost your online visibility with our SEO services and digital marketing strategies.',
      features: [
        'Keyword Research',
        'Content Strategy',
        'Link Building',
        'Analytics Setup',
      ],
      gradient: 'from-orange-500 to-red-500',
    },
    {
      icon: <BarChart3 size={48} />,
      title: 'Analytics & Reporting',
      description:
        'Data-driven insights to help you make informed decisions and track your business performance.',
      features: [
        'Custom Dashboards',
        'Real-time Analytics',
        'Performance Reports',
        'Data Visualization',
      ],
      gradient: 'from-indigo-500 to-purple-500',
    },
    {
      icon: <Shield size={48} />,
      title: 'Maintenance & Support',
      description:
        'Ongoing support and maintenance to keep your digital assets running smoothly and securely.',
      features: [
        '24/7 Monitoring',
        'Security Updates',
        'Bug Fixes',
        'Performance Optimization',
      ],
      gradient: 'from-teal-500 to-cyan-500',
    },
  ];

  const pricingTiers = [
    {
      name: 'Starter',
      price: '$2,999',
      description: 'Perfect for small businesses getting started',
      features: [
        'Up to 5 pages',
        'Responsive design',
        'Basic SEO',
        'Contact form',
        '3 months support',
      ],
      gradient: 'from-gray-400 to-gray-600',
    },
    {
      name: 'Professional',
      price: '$7,999',
      description: 'Ideal for growing businesses',
      features: [
        'Up to 15 pages',
        'Custom design',
        'Advanced SEO',
        'CMS integration',
        'E-commerce ready',
        '6 months support',
      ],
      popular: true,
      gradient: 'from-primary-500 to-primary-700',
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      description: 'Tailored solutions for large organizations',
      features: [
        'Unlimited pages',
        'Fully custom',
        'Multi-language',
        'Advanced integrations',
        'Dedicated support',
        'Priority maintenance',
      ],
      gradient: 'from-purple-500 to-pink-500',
    },
  ];

  const faqs = [
    {
      question: 'How long does a typical project take?',
      answer:
        'Project timelines vary based on scope and complexity. A simple website typically takes 4-6 weeks, while custom applications can take 8-12 weeks or more. We provide detailed timelines during the initial consultation.',
    },
    {
      question: 'Do you provide ongoing support?',
      answer:
        'Yes! We offer maintenance and support packages to ensure your digital assets stay updated, secure, and performant. Support can include updates, bug fixes, security patches, and performance monitoring.',
    },
    {
      question: 'What technologies do you use?',
      answer:
        'We use modern, industry-standard technologies including React, Node.js, TypeScript, and various databases. We choose the best stack for each project based on requirements, scalability needs, and budget.',
    },
    {
      question: 'Can you work with our existing systems?',
      answer:
        'Absolutely! We specialize in integrating new solutions with existing systems. Whether you need API integrations, data migration, or system connectivity, we can help.',
    },
    {
      question: 'What is your payment structure?',
      answer:
        'We typically work with a phased payment structure: 30% to start, 40% at milestone completion, and 30% upon final delivery. For larger projects, we can discuss custom payment terms.',
    },
    {
      question: 'Do you offer hosting services?',
      answer:
        'While we don\'t provide hosting directly, we can recommend reliable hosting providers and help you set up and deploy your project. We also offer deployment and DevOps services.',
    },
  ];

  return (
    <div className="pt-16 min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-400 rounded-full mix-blend-multiply filter blur-3xl animate-float"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Services Overview */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in">
            <div className="inline-flex items-center gap-2 mb-4">
              <Sparkles className="text-primary-600 animate-sparkle" size={24} />
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900">
                Our <span className="bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">Services</span>
              </h1>
              <Sparkles className="text-primary-600 animate-sparkle" size={24} style={{ animationDelay: '1s' }} />
            </div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive digital solutions to help your business thrive
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group fade-in bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 tech-glow border border-gray-100"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`relative mb-4 inline-block p-4 rounded-xl bg-gradient-to-br ${service.gradient} transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  <div className="text-white relative z-10">{service.icon}</div>
                  <div className="absolute inset-0 bg-white/20 rounded-xl blur-xl group-hover:blur-2xl transition-all"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-700 group-hover:text-gray-900 transition-colors">
                      <Check className="text-primary-600 flex-shrink-0 group-hover:scale-125 transition-transform" size={20} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Pricing <span className="text-primary-600">Plans</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose the plan that fits your needs, or request a custom quote
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingTiers.map((tier, index) => (
              <div
                key={index}
                className={`group fade-in bg-white p-8 rounded-xl shadow-lg border-2 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 ${
                  tier.popular
                    ? 'border-primary-600 transform scale-105 relative tech-glow'
                    : 'border-gray-200 hover:border-primary-300'
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary-600 to-purple-600 text-white text-sm font-bold px-6 py-2 rounded-full shadow-lg animate-pulse-glow">
                    <Zap size={16} className="inline mr-2" />
                    Most Popular
                  </div>
                )}
                <div className={`mb-4 inline-block p-3 rounded-lg bg-gradient-to-br ${tier.gradient} opacity-20 group-hover:opacity-30 transition-opacity`}></div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-gray-900">{tier.price}</span>
                  {tier.price !== 'Custom' && (
                    <span className="text-gray-600"> /project</span>
                  )}
                </div>
                <p className="text-gray-600 mb-6">{tier.description}</p>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 group-hover:translate-x-1 transition-transform" style={{ transitionDelay: `${idx * 0.05}s` }}>
                      <Check className="text-primary-600 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`block w-full text-center py-3 px-6 rounded-lg font-semibold transition-all duration-300 hover:scale-105 ${
                    tier.popular
                      ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg hover:shadow-xl tech-glow'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {tier.price === 'Custom' ? 'Request Quote' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50 relative z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-primary-600">Questions</span>
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about working with us
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="fade-in bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors group"
                >
                  <span className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors pr-4">
                    {faq.question}
                  </span>
                  <div className={`flex-shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}>
                    {openFaq === index ? (
                      <ChevronUp className="text-primary-600" size={24} />
                    ) : (
                      <ChevronDown className="text-gray-400 group-hover:text-primary-600" size={24} />
                    )}
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 py-4 border-t bg-gray-50">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
